<template>
    <base-dialog :loading="loading" :value="dialog" :width="460" :title="$t('common.config_playlist')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="playlist-body">
            <div v-if="contextLabel" class="playlist-context">
                <span>{{ contextLabel }}</span>
                <span class="playlist-context-warning">{{ $t('common.playlist_overwrites_individual') }}</span>
            </div>

            <div class="playlist-section" v-for="section in sections" :key="section.key">
                <div class="playlist-section-title">{{ $t(section.title) }}</div>
                <div class="playlist-question" v-for="q in section.questions" :key="q.key">
                    <div class="playlist-question-label">{{ $t(q.label) }}</div>
                    <div class="playlist-question-hint">{{ $t(q.hint) }}</div>
                    <kimsa-combobox v-if="q.type === 'select'" dense solo no-object :items="q.options"
                        :value="getValue(q.key)" @change="setValue(q.key, $event)" />
                    <kimsa-segmented v-else :items="q.options" :value="getValue(q.key)"
                        @change="setValue(q.key, $event)" />
                </div>
            </div>
        </div>

        <template v-slot:footer>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text" :disabled="disabled"
                :loading="loading" @click="config">
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import { db } from "../../utils/Firebase";
import BaseDialog from "@/components/common/BaseDialog";
import KimsaSegmented from "@/components/common/inputs/KimsaSegmented";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { DEFAULT_PLAYLIST } from "@/utils/Playlist";

const BATCH_SIZE = 400;

export default {
    name: "ConfigPlaylistModeDialog",
    components: { BaseDialog, KimsaSegmented, KimsaCombobox },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            student: null,
            students: [],
            contextLabel: null,
            limit: null,
            name: null,
            mode: null,
            anticipation: null,
            wellnessRequired: null,
            rpeRequired: null,
            sections: [
                {
                    key: 'playlist',
                    title: 'common.playlist_mode_section',
                    questions: [
                        {
                            key: 'limit',
                            label: 'common.times_per_routine',
                            hint: 'common.times_per_routine_hint',
                            options: [
                                { name: vm.$t('common.once'), value: 'once' },
                                { name: vm.$t('common.infinites'), value: 'infinites' },
                            ],
                        },
                        {
                            key: 'name',
                            label: 'common.routine_name',
                            hint: 'common.routine_name_hint',
                            options: [
                                { name: vm.$t('common.date'), value: 'date' },
                                { name: vm.$t('common.numeration'), value: 'numeration' },
                            ],
                        },
                        {
                            key: 'anticipation',
                            label: 'common.anticipation',
                            hint: 'common.anticipation_hint',
                            type: 'select',
                            options: [
                                { name: vm.$t('common.anticipation_today'), value: 'today' },
                                { name: vm.$t('common.anticipation_three_days'), value: 'three_days' },
                                { name: vm.$t('common.anticipation_seven_days'), value: 'seven_days' },
                                { name: vm.$t('common.anticipation_unlimited'), value: 'unlimited' },
                            ],
                        },
                    ],
                },
                {
                    key: 'before',
                    title: 'common.before_training',
                    questions: [
                        {
                            key: 'wellnessRequired',
                            label: 'common.wellness_before_training',
                            hint: 'common.wellness_before_training_hint',
                            options: [
                                { name: vm.$t('common.required'), value: true },
                                { name: vm.$t('common.optional'), value: false },
                            ],
                        },
                    ],
                },
                {
                    key: 'after',
                    title: 'common.after_training',
                    questions: [
                        {
                            key: 'rpeRequired',
                            label: 'common.rpe_previous_routine',
                            hint: 'common.rpe_previous_routine_hint',
                            options: [
                                { name: vm.$t('common.required_to_advance'), value: true },
                                { name: vm.$t('common.does_not_block'), value: false },
                            ],
                        },
                        {
                            key: 'mode',
                            label: 'common.mode',
                            hint: 'common.mode_hint',
                            options: [
                                { name: vm.$t('common.complete_mode'), value: 'complete' },
                                { name: vm.$t('common.only_weight'), value: 'only_weight' },
                            ],
                        },
                    ],
                },
            ],
        }
    },
    computed: {
        disabled(vm = this) {
            return !vm.limit || !vm.name || !vm.mode || !vm.anticipation
                || vm.wellnessRequired == null || vm.rpeRequired == null
        },
        payload(vm = this) {
            return {
                limit: vm.limit,
                name: vm.name,
                mode: vm.mode,
                anticipation: vm.anticipation,
                wellnessRequired: vm.wellnessRequired,
                rpeRequired: vm.rpeRequired,
            }
        },
        targetIds(vm = this) {
            if (vm.students.length) return vm.students.map(item => item.id).filter(Boolean)
            return vm.student?.id ? [vm.student.id] : []
        },
    },
    methods: {
        show(student) {
            const vm = this
            vm.setDefaults(student)
            vm.dialog = true
        },
        showForGroup(students, label) {
            const vm = this
            vm.reset()
            vm.students = students || []
            vm.contextLabel = label || null
            vm.setDefaults(vm.students[0] || null)
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.student = null
            vm.students = []
            vm.contextLabel = null
            vm.limit = null
            vm.name = null
            vm.mode = null
            vm.anticipation = null
            vm.wellnessRequired = null
            vm.rpeRequired = null
        },
        getValue(key) {
            return this[key]
        },
        setValue(key, value) {
            this[key] = value
        },
        setDefaults(student) {
            const vm = this
            if (!vm.students.length) vm.student = student
            const playlist = student?.playlist || {}
            vm.limit = playlist.limit || DEFAULT_PLAYLIST.limit
            vm.name = playlist.name || DEFAULT_PLAYLIST.name
            vm.mode = playlist.mode || DEFAULT_PLAYLIST.mode
            vm.anticipation = playlist.anticipation || DEFAULT_PLAYLIST.anticipation
            vm.wellnessRequired = playlist.wellnessRequired ?? DEFAULT_PLAYLIST.wellnessRequired
            vm.rpeRequired = playlist.rpeRequired ?? DEFAULT_PLAYLIST.rpeRequired
        },
        async config() {
            const vm = this
            const ids = vm.targetIds
            if (!ids.length) return
            await vm.withLoading(async () => {
                for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                    const batch = db.batch()
                    ids.slice(i, i + BATCH_SIZE)
                        .forEach(id => batch.update(db.collection('users').doc(id), { playlist: vm.payload }))
                    await batch.commit()
                }
                vm.$emit('config', vm.payload, ids)
                vm.hide()
            })
        }
    },
}
</script>

<style scoped>
.playlist-body {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.playlist-context {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: var(--v-accent-base);
    background: var(--v-surface2-base);
    border-radius: 8px;
    padding: 10px 12px;
}

.playlist-context-warning {
    font-weight: 400;
    line-height: 1.4;
    color: var(--v-txtMuted-base);
}

.playlist-section {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.playlist-section-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.playlist-question-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    margin-bottom: 4px;
}

.playlist-question-hint {
    font-size: 12px;
    line-height: 1.5;
    color: var(--v-txtMuted-base);
    margin-bottom: 10px;
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>
